import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBBadge,
  MDBBtn,
  MDBTable,
} from "mdbreact";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from "chart.js";
import { useDispatch, useSelector } from "react-redux";
import { Categories, Services } from "../../../../services/fakeDb";
import { SERVICE_CENSUS } from "../../../../services/redux/slices/finance/bookkeeping/remittances";
import {
  axioKit,
  fullName,
  getAge,
  timeFormat,
} from "../../../../services/utilities";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const OVERVIEW_URL = "commerce/pos/services/deals/lab_overview";
const REFRESH_MS = 60 * 1000;

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// the lab pipeline, in the order a patient goes thru it
const STAGES = [
  {
    key: "waiting",
    label: "Waiting for extraction",
    hint: "Paid, specimen not yet taken",
    icon: "hourglass-half",
    color: "warning",
  },
  {
    key: "processing",
    label: "In process",
    hint: "Specimen taken, results not yet done",
    icon: "vials",
    color: "primary",
  },
  {
    key: "ready",
    label: "Ready to release",
    hint: "All results done and signed",
    icon: "file-medical",
    color: "success",
  },
  {
    key: "outsourced",
    label: "Outsourced",
    hint: "Sent to a partner laboratory",
    icon: "share-alt",
    color: "secondary",
  },
];

const BAR_COLOR = "rgba(41, 121, 255, 0.85)";

const barOptions = (indexAxis = "x") => ({
  indexAxis,
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  datasets: { bar: { borderRadius: 4, maxBarThickness: 28 } },
  scales: {
    x: {
      grid: { display: indexAxis === "y", color: "rgba(0,0,0,0.06)" },
      ticks: { color: "#7e8591", precision: 0 },
      beginAtZero: true,
    },
    y: {
      grid: { display: indexAxis === "x", color: "rgba(0,0,0,0.06)" },
      ticks: { color: "#7e8591", precision: 0 },
      beginAtZero: true,
    },
  },
});

const hourLabel = (hour) =>
  `${hour % 12 || 12}${hour < 12 ? "am" : "pm"}`;

const waited = (date) => {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(date)) / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d`;
};

const categoryName = (abbr) =>
  Categories.find((category) => category.abbr === abbr)?.name || abbr || "";

const StatCard = ({ label, hint, icon, color, value, note, isLoading }) => (
  <MDBCard className="h-100">
    <MDBCardBody className="d-flex align-items-center py-3">
      <MDBBtn
        tag="span"
        floating
        color={color}
        className="m-0 mr-3 flex-shrink-0"
        style={{ cursor: "default" }}
      >
        <MDBIcon icon={icon} />
      </MDBBtn>
      <div style={{ minWidth: 0 }}>
        <div className="h3 font-weight-bold mb-0">
          {isLoading ? "…" : value.toLocaleString()}
        </div>
        <div className="font-weight-bold small">{label}</div>
        <small className="text-muted d-block text-truncate" title={hint}>
          {note || hint}
        </small>
      </div>
    </MDBCardBody>
  </MDBCard>
);

export default function Dashboard() {
  const dispatch = useDispatch();
  const today = new Date();
  const currentYear = today.getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [overview, setOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [queue, setQueue] = useState("waiting");

  const { token, activePlatform = {} } = useSelector(({ auth }) => auth);
  const {
    serviceCensus = [],
    serviceCensusSummary = {},
    serviceCensusRange: range,
    isLoading: censusLoading,
  } = useSelector(({ remittances }) => remittances);

  const branchId = activePlatform?.branchId || "";
  const branch = activePlatform?.branch || {};

  const loadOverview = useCallback(() => {
    if (!token || !branchId) return;
    setIsLoading(true);
    axioKit
      .universal(OVERVIEW_URL, token, { branchId })
      .then(({ payload }) => {
        setOverview(payload);
        setError("");
        setUpdatedAt(new Date());
      })
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [token, branchId]);

  // the board is live, refresh it every minute
  useEffect(() => {
    loadOverview();
    const timer = setInterval(loadOverview, REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadOverview]);

  useEffect(() => {
    if (!token || !branchId) return;
    dispatch(
      SERVICE_CENSUS({
        token,
        key: { branchId, month: selectedMonth, year: selectedYear },
      }),
    );
  }, [branchId, dispatch, selectedMonth, selectedYear, token]);

  const {
    summary = {},
    sections = [],
    hourly = [],
    patients = [],
  } = overview || {};

  // only the hours the lab was busy, from the first to the last arrival
  const hourlyChart = useMemo(() => {
    const busy = hourly
      .map((count, hour) => ({ count, hour }))
      .filter(({ count }) => count > 0);
    if (!busy.length) return null;
    const from = Math.min(busy[0].hour, 7),
      to = Math.max(busy[busy.length - 1].hour, 17);
    const hours = hourly.slice(from, to + 1).map((count, i) => ({
      count,
      hour: from + i,
    }));
    return {
      labels: hours.map(({ hour }) => hourLabel(hour)),
      datasets: [
        {
          label: "Patients",
          data: hours.map(({ count }) => count),
          backgroundColor: BAR_COLOR,
        },
      ],
    };
  }, [hourly]);

  // the monthly census, laboratory tests only
  const labCensus = useMemo(
    () =>
      serviceCensus
        .filter(({ serviceId }) => Services.find(serviceId)?.department === "LAB")
        .map((service) => {
          const found = Services.find(service.serviceId);
          return {
            ...service,
            name: found?.name || service.description || `Service #${service.serviceId}`,
            abbr: found?.abbreviation || "",
          };
        }),
    [serviceCensus],
  );
  const labTestsTotal = labCensus.reduce((sum, { count = 0 }) => sum + count, 0);
  const topTests = labCensus.slice(0, 10);
  const topChart = {
    labels: topTests.map(({ abbr, name }) => abbr || name),
    datasets: [
      {
        label: "Times availed",
        data: topTests.map(({ count }) => count),
        backgroundColor: BAR_COLOR,
      },
    ],
  };

  const { current = {}, previous = {} } = serviceCensusSummary || {};
  // a running month is compared with the same days of last month: "Sep 1–16"
  const shortDate = (iso, options) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString("en-PH", options);
  const compareLabel = range?.isRunning
    ? `${shortDate(range.previous.from, { month: "short", day: "numeric" })}–${shortDate(range.previous.to, { day: "numeric" })}`
    : "last month";
  const monthStats = [
    { label: "Lab tests availed", value: labTestsTotal },
    {
      label: "Patients (all services)",
      value: current.purchases || 0,
      last: previous.purchases || 0,
    },
    {
      label: "Walk-in / OPD visits",
      value: current.opdWi || 0,
      last: previous.opdWi || 0,
    },
    {
      label: "Referral visits",
      value: current.insources || 0,
      last: previous.insources || 0,
    },
    {
      label: "Outsourced",
      value: current.outsources || 0,
      last: previous.outsources || 0,
    },
  ];

  const queueList = patients.filter(({ stage }) => stage === queue);
  const firstLoad = isLoading && !overview;

  return (
    <MDBContainer fluid className="mb-5">
      {/* Title */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div>
          <h4 className="font-weight-bold mb-0">Laboratory Overview</h4>
          <small className="text-muted">
            {branch?.displayname || branch?.name || "No active branch"} ·{" "}
            {today.toLocaleDateString("en-PH", {
              weekday: "long",
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </small>
        </div>
        <div className="d-flex align-items-center">
          {updatedAt && (
            <small className="text-muted mr-2">
              Updated {timeFormat(updatedAt)}
            </small>
          )}
          <MDBBtn
            size="sm"
            color="info"
            className="m-0"
            onClick={loadOverview}
            disabled={isLoading}
          >
            <MDBIcon icon="sync" spin={isLoading} className="mr-1" />
            Refresh
          </MDBBtn>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger py-2">
          Could not load the laboratory board: {error}
        </div>
      )}

      {/* Pipeline */}
      <MDBRow className="mb-1">
        <MDBCol xl="4" md="12" className="mb-3">
          <StatCard
            label="Lab patients today"
            hint="Patients with laboratory tests today"
            icon="user-injured"
            color="info"
            value={summary.today || 0}
            note={
              summary.carryOver
                ? `+${summary.carryOver} unfinished from the past week`
                : "Patients with laboratory tests today"
            }
            isLoading={firstLoad}
          />
        </MDBCol>
        {STAGES.map((stage) => (
          <MDBCol key={stage.key} xl="2" md="6" className="mb-3">
            <StatCard
              {...stage}
              value={summary[stage.key] || 0}
              isLoading={firstLoad}
            />
          </MDBCol>
        ))}
      </MDBRow>

      <MDBRow>
        {/* Patient queue */}
        <MDBCol xl="7" className="mb-4">
          <MDBCard className="h-100">
            <MDBCardBody>
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
                <h6 className="font-weight-bold mb-2">Patient queue</h6>
                <div className="d-flex flex-wrap">
                  {STAGES.map(({ key, label, color }) => (
                    <button
                      key={key}
                      type="button"
                      className={`btn btn-sm m-0 ml-1 mb-1 px-2 ${
                        queue === key ? `btn-${color}` : `btn-outline-${color}`
                      }`}
                      onClick={() => setQueue(key)}
                    >
                      {label}
                      <MDBBadge color="light" className="ml-1 text-dark" pill>
                        {summary[key] || 0}
                      </MDBBadge>
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ maxHeight: 420, overflowY: "auto" }}>
                <MDBTable small hover className="mb-0">
                  <thead className="grey lighten-4">
                    <tr>
                      <th>Time</th>
                      <th>Patient</th>
                      <th>Tests</th>
                      <th className="text-right">Waiting</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!queueList.length && (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-4">
                          {firstLoad
                            ? "Loading…"
                            : "No patient in this stage right now."}
                        </td>
                      </tr>
                    )}
                    {queueList.map(
                      ({ _id, customer, createdAt, category, forms, isCarryOver }) => (
                        <tr key={_id}>
                          <td className="text-nowrap align-middle">
                            {timeFormat(createdAt)}
                            {isCarryOver && (
                              <small className="d-block text-danger">
                                {new Date(createdAt).toLocaleDateString("en-PH", {
                                  month: "short",
                                  day: "numeric",
                                })}
                              </small>
                            )}
                          </td>
                          <td className="align-middle">
                            <span className="font-weight-bold">
                              {fullName(customer?.fullName) || "Unknown patient"}
                            </span>
                            <small className="d-block text-muted">
                              {[
                                customer?.dob && `${getAge(customer.dob)}`,
                                customer?.isMale === undefined
                                  ? ""
                                  : customer.isMale
                                    ? "Male"
                                    : "Female",
                                categoryName(category),
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </small>
                          </td>
                          <td className="align-middle">
                            {forms.length ? (
                              forms.map(({ name, done }) => (
                                <MDBBadge
                                  key={name}
                                  color={done ? "success" : "light"}
                                  className={`mr-1 mb-1 ${done ? "" : "text-dark"}`}
                                  title={done ? "Result done" : "Result pending"}
                                >
                                  {done && <MDBIcon icon="check" className="mr-1" />}
                                  {name}
                                </MDBBadge>
                              ))
                            ) : (
                              <small className="text-muted">No form yet</small>
                            )}
                          </td>
                          <td className="text-right text-nowrap align-middle">
                            <span className={isCarryOver ? "text-danger" : ""}>
                              {waited(createdAt)}
                            </span>
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </MDBTable>
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>

        <MDBCol xl="5">
          {/* Sections */}
          <MDBCard className="mb-4">
            <MDBCardBody>
              <h6 className="font-weight-bold mb-3">Workload by section</h6>
              {!sections.length && (
                <div className="text-center text-muted py-3">
                  {firstLoad ? "Loading…" : "No laboratory work today yet."}
                </div>
              )}
              {sections.map(({ name, total, done, tracked }) => {
                const percent = total ? Math.round((done / total) * 100) : 0;
                return (
                  <div key={name} className="mb-2">
                    <div className="d-flex justify-content-between small">
                      <span className="font-weight-bold">{name}</span>
                      <span className="text-muted">
                        {tracked
                          ? `${done} of ${total} done · ${total - done} pending`
                          : `${total} test(s) · not tracked`}
                      </span>
                    </div>
                    <div
                      className="progress"
                      style={{ height: 8, borderRadius: 4 }}
                      role="progressbar"
                      aria-valuenow={percent}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <div
                        className={`progress-bar ${percent === 100 ? "bg-success" : "bg-primary"}`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </MDBCardBody>
          </MDBCard>

          {/* Arrivals */}
          <MDBCard className="mb-4">
            <MDBCardBody>
              <h6 className="font-weight-bold mb-1">Arrivals today</h6>
              <small className="text-muted d-block mb-2">
                Lab patients per hour, to see the rush hours
              </small>
              {hourlyChart ? (
                <div style={{ height: 180 }}>
                  <Bar data={hourlyChart} options={barOptions("x")} />
                </div>
              ) : (
                <div className="text-center text-muted py-3">
                  No arrivals yet today.
                </div>
              )}
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>

      {/* Month */}
      <MDBCard>
        <MDBCardBody>
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
            <div>
              <h6 className="font-weight-bold mb-0">Monthly census</h6>
              <small className="text-muted">
                Counts of services availed, not sales
                {range?.isRunning &&
                  ` · ${MONTH_LABELS[selectedMonth - 1]} is still running, so it is compared with the same days of last month`}
              </small>
            </div>
            <div className="d-flex">
              <select
                className="form-control form-control-sm mr-2"
                value={selectedMonth}
                onChange={({ target }) => setSelectedMonth(Number(target.value))}
              >
                {MONTH_LABELS.map((label, index) => (
                  <option key={label} value={index + 1}>
                    {label}
                  </option>
                ))}
              </select>
              <select
                className="form-control form-control-sm"
                value={selectedYear}
                onChange={({ target }) => setSelectedYear(Number(target.value))}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <MDBRow className="mb-3">
            {monthStats.map(({ label, value, last }) => {
              const diff = last === undefined ? null : value - last;
              return (
                <MDBCol key={label} className="mb-2 col-lg" md="4">
                  <div className="p-2 border rounded h-100">
                    <div className="h5 font-weight-bold mb-0">
                      {censusLoading ? "…" : value.toLocaleString()}
                    </div>
                    <small className="d-block">{label}</small>
                    {diff !== null && !censusLoading && (
                      <small
                        className={
                          diff > 0
                            ? "text-success"
                            : diff < 0
                              ? "text-danger"
                              : "text-muted"
                        }
                      >
                        <MDBIcon
                          icon={diff > 0 ? "arrow-up" : diff < 0 ? "arrow-down" : "minus"}
                          className="mr-1"
                        />
                        {Math.abs(diff).toLocaleString()} vs {compareLabel} (
                        {last.toLocaleString()})
                      </small>
                    )}
                  </div>
                </MDBCol>
              );
            })}
          </MDBRow>

          <MDBRow>
            <MDBCol lg="6" className="mb-3">
              <div className="small font-weight-bold mb-2">
                Top 10 laboratory tests
              </div>
              {topTests.length ? (
                <div style={{ height: 300 }}>
                  <Bar data={topChart} options={barOptions("y")} />
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  {censusLoading
                    ? "Loading…"
                    : `No laboratory test availed in ${MONTH_LABELS[selectedMonth - 1]} ${selectedYear}.`}
                </div>
              )}
            </MDBCol>
            <MDBCol lg="6">
              <div style={{ maxHeight: 330, overflowY: "auto" }}>
                <MDBTable small hover className="mb-0">
                  <thead className="grey lighten-4">
                    <tr>
                      <th>#</th>
                      <th>Laboratory test</th>
                      <th className="text-right">Walk-in</th>
                      <th className="text-right">Referral</th>
                      <th className="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!labCensus.length && (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-3">
                          {censusLoading ? "Loading…" : "No data for this month."}
                        </td>
                      </tr>
                    )}
                    {labCensus.map(
                      ({ serviceId, name, abbr, walkin = 0, referral = 0, count = 0 }, index) => (
                        <tr key={serviceId}>
                          <td className="text-muted">{index + 1}</td>
                          <td>
                            {name}
                            {abbr && (
                              <small className="text-muted ml-1">({abbr})</small>
                            )}
                          </td>
                          <td className="text-right">{walkin.toLocaleString()}</td>
                          <td className="text-right">{referral.toLocaleString()}</td>
                          <td className="text-right font-weight-bold">
                            {count.toLocaleString()}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </MDBTable>
              </div>
            </MDBCol>
          </MDBRow>
        </MDBCardBody>
      </MDBCard>
    </MDBContainer>
  );
}
